import React, {useEffect} from 'react';
import {ColorModeScript, TempoProvider, useColorMode} from '@tempo/core';
import theme from '@deezer/next/shared/theme/index';
import {useD4CToolsValue} from '@deezer/next/components/D4CTools/D4CToolsContext';
import {ConfigKeys} from '@deezer/next/components/D4CTools/types';
import CSSReset from '@components/CSSReset';
import type {ThemePreference} from '@deezer/next/services/storage/shared/types';

const ForcedColorModeSetter: React.FC<
	React.PropsWithChildren<{
		mode: ThemePreference;
	}>
> = ({children, mode}) => {
	const {colorMode, setColorMode} = useColorMode();

	useEffect(() => {
		if (mode !== colorMode) {
			setColorMode(mode);
		}
	}, [mode, colorMode, setColorMode]);

	return <>{children}</>;
};

const ThemeProvider: React.FC<
	React.PropsWithChildren<{
		mode?: ThemePreference;
	}>
> = ({children, mode}) => {
	const resetCSS = useD4CToolsValue(ConfigKeys.RESET_CSS);
	const forcedThemeMode = useD4CToolsValue(ConfigKeys.THEME_MODE);

	const initialColorMode = mode || theme.config.initialColorMode || 'light';
	const hasForcedMode = forcedThemeMode !== 'default';

	return (
		<>
			<ColorModeScript initialColorMode={initialColorMode} />
			<TempoProvider
				theme={theme}
				resetCSS={false}
				cssVarsRoot=":host, :root"
				toastOptions={{
					defaultOptions: {
						duration: 9000,
						isClosable: true,
					},
				}}
			>
				{resetCSS && <CSSReset />}
				{hasForcedMode ? (
					<ForcedColorModeSetter mode={forcedThemeMode as ThemePreference}>
						{children}
					</ForcedColorModeSetter>
				) : (
					children
				)}
			</TempoProvider>
		</>
	);
};

export default ThemeProvider;
